<template>
  <v-card>
    <v-card-title :class="contractColor">
      <span class="headline white--text">{{title}}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text>
      <div style="width: 100%;" ref="imageContainer">
        <img v-if="qrCodeLoader.success" :src="dataUrl" :draggable="false" :style="imageStyle"/>
      </div>
      <div class="d-flex align-center justify-center" style="gap: 12px;">
        <v-btn outlined color="primary" :loading="downloading" :disabled="downloading" @click="toPdf()" id="download-contract-qr-code-button">
          <v-icon class="mr-1">mdi-download</v-icon>
          {{$t('downloadQrCodeSheet')}}
        </v-btn>
        <v-select
          v-if="showGatePassTypeSelect"
          id="contract-qr-code-gate-pass-type"
          v-model="selectedGatePassType"
          :items="gatePassOptions"
          :label="$t('gatePassType')"
          dense
          outlined
          hide-details
          style="max-width: 160px; min-width: 120px;"
        />
        <v-checkbox
          v-if="includeProducts"
          v-model="listProducts"
          :label="$t('listProducts')"
          dense
          hide-details
          class="mt-0 pt-0"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ResourceLoader } from '@/utils/ResourceLoader'
import { generateContractIdSheetPDF, getContractQrCodeData } from '@/utils/pdf-formatters/ContractIdentification'
import { getLegnaQrCodeUrl } from '@/utils/LegnaQrCode'
import { tickTock, colorClassForContractMode, contractTypeHasGatePass } from '@/utils/componentHelpers.js'
import { ContractType, GatePassType } from '@/utils/Enumerations.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'

export default {
  name: 'ContractQrCode',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    propContract: {
      type: Number,
      required: true
    },
    contractMode: {
      type: Object
    }
  },

  data: () => ({
    dataUrl: undefined,
    isMounted: false,
    imageSize: 0,
    imageOffset: { x: 0, y: 0 },
    qrCodeLoader: ResourceLoader.empty,
    contract: null,
    selectedGatePassType: null,
    listProducts: localStorage.getItem(LocalStorageKeys.CONTRACT_QR_LIST_PRODUCTS) === 'true',
    downloading: false
  }),

  watch: {
    vuetifyBreakpointName () {
      this.repeatInterval(this.onResize, 500, 3)
    },
    dialogFullscreen () {
      this.repeatInterval(this.onResize, 400, 3)
    },
    selectedGatePassType () {
      this.qrCodeData = getContractQrCodeData(this.contract.contractId, this.companyInfo.code, this.qrCodeParam(this.contract))
      this.setQrCode()
    },
    listProducts (value) {
      localStorage.setItem(LocalStorageKeys.CONTRACT_QR_LIST_PRODUCTS, value)
    }
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('user', ['companyInfo']),

    title () {
      return this.$t('contractQrCode')
    },

    imageStyle () {
      return `
        image-rendering: pixelated;
        display: inline-block;
        margin-left: auto;
        margin-right: auto;
        transform: translate(${this.imageOffset.x}px, ${0}px);
      `
    },

    vuetifyBreakpointName () {
      return this.$vuetify?.breakpoint?.name ?? ''
    },

    dialogFullscreen () {
      return this.topDialogState?.fullscreen ?? false
    },

    contractColor () {
      return colorClassForContractMode(this.contractMode.value)
    },

    allowPickupLoads () {
      return this.contract?.allowPickupLoads ?? false
    },

    showGatePassTypeSelect () {
      switch (this.contract?.type) {
        case ContractType.ByproductSale.value:
        case ContractType.LogYardSale.value:
          return this.allowPickupLoads
        default:
          return false
      }
    },

    includeProducts () {
      return this.contract?.type === ContractType.Production.value &&
        !this.contract?.isExternal &&
        !!this.contract?.products?.length
    },

    gatePassOptions () {
      return [
        { text: this.$t('departure'), value: GatePassType.Departure.value },
        { text: this.$t('pickupOnly'), value: GatePassType.Pickup.value }
      ]
    },

    selectedGatePassTypeText () {
      if (!contractTypeHasGatePass(this.contract?.type)) return ''
      return this.gatePassType === GatePassType.Pickup.value
        ? this.$t('pickupOnly')
        : this.$t('departure')
    },

    gatePassType () {
      switch (this.contract?.type) {
        case ContractType.ByproductSale.value:
          return this.allowPickupLoads
            ? (this.selectedGatePassType ?? GatePassType.Pickup.value)
            : GatePassType.Departure.value
        case ContractType.LogYardSale.value:
          return this.allowPickupLoads
            ? (this.selectedGatePassType ?? GatePassType.Departure.value)
            : GatePassType.Departure.value
        case ContractType.Transfer.value:
          // The kiosk rejects pickup-type transfer passes.
          return GatePassType.Departure.value
        default:
          return null
      }
    }
  },

  created () {
    this.qrCodeLoader = new ResourceLoader(async () => {
      this.contract = await this.getContract(this.propContract)

      if (this.selectedGatePassType === null) {
        if (this.contract?.type === ContractType.ByproductSale.value && this.contract?.allowPickupLoads) {
          this.selectedGatePassType = GatePassType.Pickup.value
        } else if (this.contract?.type === ContractType.LogYardSale.value) {
          this.selectedGatePassType = GatePassType.Departure.value
        }
      }

      this.qrCodeData = getContractQrCodeData(this.contract.contractId, this.companyInfo.code, this.qrCodeParam(this.contract))
      await this.calculateImageSize(this.$refs.imageContainer)
      this.setQrCode()
    })
    window.addEventListener(
      'resize',
      () => {
        this.onResize()
        this.repeatInterval(this.onResize, 400, 2)
      },
      { passive: true }
    )
  },

  async mounted () {
    if (this.$refs.canvasRef) {
      this.$refs.canvasRef.width = 500
      this.$refs.canvasRef.height = 500
    }
    await this.calculateImageSize(this.$refs.imageContainer)
    await this.qrCodeLoader.load()
  },

  beforeDestroy () {
    window.removeEventListener(
      'resize',
      () => {
        this.onResize()
        this.repeatInterval(this.onResize, 400, 2)
      },
      { passive: true }
    )
  },

  methods: {
    ...mapActions('contract', ['getContract']),

    tickTock,

    async toPdf () {
      this.downloading = true
      try {
        const text = getContractQrCodeData(this.contract.contractId, this.companyInfo.code, this.qrCodeParam(this.contract))
        const qrCode = await getLegnaQrCodeUrl(250, text)
        await generateContractIdSheetPDF(this.contract, qrCode, this.selectedGatePassTypeText, this.includeProducts && this.listProducts)
      } catch (e) {
        console.error(e)
        this.setSnackError(this.$t('downloadQrCodeSheetError'))
      } finally {
        this.downloading = false
      }
    },

    async calculateImageSize () {
      const container = this.$refs.imageContainer
      this.tickTock(
        () => {
          this.imageSize = (container)
            ? Math.min(container.clientWidth, window.outerHeight - 230) - 200
            : 0
        },
        () => {
          this.imageOffset = {
            x: (container) ? (container.clientWidth - this.imageSize) / 2 : 0,
            y: (container) ? (container.clientHeight - this.imageSize) / 2 : 0
          }
        }
      )
    },

    repeatInterval (action, interval, n) {
      const intervalId = setInterval(() => {
        action()
        if (--n === 0) clearInterval(intervalId)
      }, interval)
    },

    async setQrCode () {
      this.dataUrl = await getLegnaQrCodeUrl(this.imageSize, this.qrCodeData)
    },

    async onResize () {
      await this.calculateImageSize()
      await this.setQrCode()
    },

    qrCodeParam (contract) {
      if (!contractTypeHasGatePass(contract?.type)) return ''
      return JSON.stringify({ gpt: this.gatePassType })
    }
  }
}
</script>
